import _ from 'lodash';

/**
 * Chart Colours
 */
export const chartColours = [
    '#45BFFC',
    '#3DF2C3',
    '#CD5CD8',
    '#EB1458',
    '#7DD3F4',
    '#94F7DB',
    '#E18FEF',
    '#F94B8A',
    '#4A89E5',
    '#EA7A68',
    '#9A4CD9',
    '#BF104F',
    '#422960',
    '#007180',
    '#2ECE9D'
];


const claimCostLabels = ['Damages Reserve', 'Claimant Costs Reserve', 'Defence Costs Reserve'];

/**
 * Get Chart Data for Trends In Reserves
 */
export const trendsChartData = (state, type) => {
    let chartData = state.trendReserve.charts;
    const diseaseType = state.filters.diseaseType;
    let isDiseaseTypeSet = diseaseType !== null;
    let data = [];
    const claimCost = state.filters.claimCost;
    const period = state.trendReserve.period;
    let labels = [];

    if (Object.keys(chartData).length) {
        for (let key in chartData[claimCost][period][type].data) {
            labels.push(key);
        }
        data = formatTrendData(
            chartData,
            isDiseaseTypeSet,
            claimCost,
            period,
            type,
            diseaseType
        );
    }

    return {data, labels, type};
};

function extractChartData(chartData) {
    let data = [];
    if (Object.keys(chartData).length) {
        for (let key in chartData) {
            if (chartData.hasOwnProperty(key)) {
                data.push(chartData[key]);
            }
        }
    }
    return data;
}

function extractChartLabels(chartData) {
    let labels = [];
    if (Object.keys(chartData).length) {
        for (let key in chartData) {
            if (chartData.hasOwnProperty(key)) {
                labels.push(key);
            }
        }
    }
    return labels;
}

function prepareChartDataSet(data, colour, label = '') {
    return {
        label,
        data,
        backgroundColor: colour,
        borderWidth: 0,
    };
}

function getDataSets(isDiseaseTypeSet, chart, type, cost) {
    let dataSets = [];
    if (isDiseaseTypeSet) {
        dataSets = [
            prepareChartDataSet(
                extractChartData(chart.damages[type].chartData),
                chartColours[0],
                'Damages Reserve'
            ),
            prepareChartDataSet(
                extractChartData(chart.claimantCosts[type].chartData),
                chartColours[1],
                'Claimant Costs Reserve'
            ),
            prepareChartDataSet(
                extractChartData(chart.defenceCosts[type].chartData),
                chartColours[2],
                'Defence Costs Reserve'
            ),
        ];
    } else {
        dataSets.push(
            prepareChartDataSet(
                extractChartData(chart[cost][type].chartData),
                chartColours
            )
        );
    }
    return dataSets;
}

function getPieDataSets(isDiseaseTypeSet, chart, type, cost) {
    let dataSets = [];
    if (isDiseaseTypeSet) {
        dataSets = [
            prepareChartDataSet(
                extractChartData(chart.damages[type].chartData)
                    .concat(extractChartData(chart.claimantCosts[type].chartData))
                    .concat(extractChartData(chart.defenceCosts[type].chartData)),
                chartColours
            ),
        ];
    } else {
        dataSets.push(
            prepareChartDataSet(
                extractChartData(chart[cost][type].chartData),
                chartColours
            )
        );
    }
    return dataSets;
}


function labels(isDiseaseTypeSet, chart, cost, type) {
    return isDiseaseTypeSet ?
        claimCostLabels :
        extractChartLabels(chart[cost][type].chartData);
}

/**
 * Get Chart Data for Average Reserves
 */
export const averageChartData = (state, type) => {
    let chart = state.averageReserve.charts;
    let isDiseaseTypeSet = state.filters.diseaseType !== null;
    let cost = state.filters.claimCost;
    let dataSets = getDataSets(isDiseaseTypeSet, chart, type, cost);
    let pieDataSets = getPieDataSets(isDiseaseTypeSet, chart, type, cost);
    let hasData = !!dataSets[0].data.length;

    return {
        dataSets,
        pieDataSets,
        labels: labels(isDiseaseTypeSet, chart, cost, type),
        type,
        hasData,
        average: state.averageReserve.charts.all.overallAverage,
    };
};

/**
 * Get Chart Data for Total Reserves
 */
export const totalChartData = (state, type) => {
    let chart = state.totalReserve.charts;
    let isDiseaseTypeSet = state.filters.diseaseType !== null;
    let cost = state.filters.claimCost;
    let dataSets = getDataSets(isDiseaseTypeSet, chart, type, cost);
    let pieDataSets = getPieDataSets(isDiseaseTypeSet, chart, type, cost);
    let hasData = !!dataSets[0].data.length;

    return {
        dataSets,
        pieDataSets,
        labels: labels(isDiseaseTypeSet, chart, cost, type),
        type,
        hasData
    };
};

function trendByDiseaseType(chartData, diseaseType = null) {
    let newItem = {};
    let i = 0;

    for (let date in chartData) {
        for (let disease in chartData[date]) {
            if (!chartData[date].hasOwnProperty(disease) || diseaseType !== null && diseaseType !== disease) {
                continue;
            }

            if (!newItem[disease]) {
                newItem[disease] = {
                    label: disease,
                    type: 'line',
                    backgroundColor: [
                        chartColours[i]
                    ],
                    borderColor: [
                        chartColours[i]
                    ],
                    fill: false,
                    data: []
                };
                i++;
            }

            newItem[disease].data.push(chartData[date][disease]);
        }
    }
    return newItem;
}

function trendByCostType(chartData, diseaseType, period, type) {
    let data = [
        trendByDiseaseType(chartData.damages[period][type].data)[diseaseType],
        trendByDiseaseType(chartData.claimantCosts[period][type].data)[diseaseType],
        trendByDiseaseType(chartData.defenceCosts[period][type].data)[diseaseType],
    ];
    if (!data[0]) {
        return [];
    }
    data[0].label = claimCostLabels[0];
    data[1].label = claimCostLabels[1];
    data[2].label = claimCostLabels[2];
    data[0].backgroundColor = [chartColours[0]];
    data[1].backgroundColor = [chartColours[1]];
    data[2].backgroundColor = [chartColours[2]];
    data[0].borderColor = [chartColours[0]];
    data[1].borderColor = [chartColours[1]];
    data[2].borderColor = [chartColours[2]];
    return data;
}

/**
 * Format Chart Data for Trends In Reserves
 */
const formatTrendData = (chartData, isDiseaseTypeSelected, claimCost, period, type, diseaseType) => {
    const data = isDiseaseTypeSelected && type !== 'number' ?
        trendByCostType(chartData, diseaseType, period, type) :
        trendByDiseaseType(chartData[claimCost][period][type].data);
    let values = _.values(data);
    let maxLength = values.reduce((max, item) => {
        return Math.max(max, item.data.length);
    }, 0);

    return values.map(item => {
        while (item.data.length < maxLength) {
            item.data.push(0);
        }
        return item;
    });
};

export const averageReserveLineChartData = (state, type) => {
    let chartData = state.averageReserves.graphs.chartData;
    let data = [];

    if (Object.keys(chartData).length) {
        for (let key in chartData) {
            data.push((typeof chartData[key][type] !== 'undefined') ? chartData[key][type] : 0);
        }
    }

    data.splice(-1, 1);

    return data;
};



// WEBPACK FOOTER //
// ./src/store/modules/shared/chartData.js